import {
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import styles from "../addBulkCart.module.scss";
import { ToastNotification } from "../../../../common/components/toastNotification/ToastNotification";
import errorToast from "../../../../assets/images/errorToast.svg";
import successToast from "../../../../assets/images/successToast.svg";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { updateIsLoading } from "../../../../reducerSlices/loaderSlice";
import { constants } from "../../../../common/constants/constants";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAddBulkCartContext } from "../context/addBulkCartContext";
import Button from "../../../../common/components/button/Button";

function BuyerCartForm() {
  const [toastData, setToastData] = useState(constants.defaultToastData);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const { accessToken, outlets } = useAddBulkCartContext();

  useEffect(() => {
    if (accessToken) {
      setToastData({
        message: "Buyer authenticated successfully",
        status: "Success",
      });
      setTimeout(() => {
        setToastData(constants.defaultToastData);
      }, 2000);
    }
  }, [accessToken]);

  const schema = yup.object().shape({
    outletId: yup.string().required("Select Outlet"),
    file: yup
      .mixed()
      .required("Please upload a file")
      .test(
        "fileType",
        "Unsupported File Format. Please upload a .pdf",
        (value: any) => {
          if (!value || value.length === 0) return false; // No file selected
          const supportedFormats = [".pdf   ", "application/pdf"];
          return supportedFormats.includes(value[0]?.type);
        }
      ),
  });

  const {
    formState: { errors, isSubmitted },
    handleSubmit,
    setValue,
    watch,
    // getValues,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: any) => {
    dispatch(updateIsLoading(true));

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("cartId", data.outletId);

      // Append the actual file
      const fileList = watch("file") as FileList;
      if (fileList && fileList.length > 0) {
        formData.append("file", fileList[0]);
      }
      console.log("FormData access:", accessToken);
      // using fetch to upload file to n8n webhook endpoint, using axios will overwrite auth header since the interceptor is setting auth header
      const response = await fetch(
        "https://watermelon-market.app.n8n.cloud/webhook/9680a4fe-f700-4033-a419-f5300875449e", // prod webhook
        // "https://watermelon-market.app.n8n.cloud/webhook-test/3c4cb581-02b1-4461-84aa-2560a74647ba", // test webhook uat
        {
          method: "POST",
          headers: {
            authorization: `Bearer ${accessToken}`,
            "n8n-access-token":
              "88a79dc4cd92362aef7fe8a9b62fa8fd9bfcffff0a8d5f339323d7ca349b9008a3b26372ee8d8af594dc7a4f40c56b39190164ccbfd79155504638f539f57f01",
          },
          body: formData,
        }
      );

      if (response.ok) {
        setToastData({
          message:
            "The File has been uploaded and Automation is running, Result will be pushed on slack after 10 mins",
          status: "Success",
        });
        dispatch(updateIsLoading(false));
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setToastData({
        message: "Upload failed. Please try again.",
        status: "Fail",
      });
    } finally {
      setTimeout(() => {
        setToastData(constants.defaultToastData);
      }, 10000);
      reset();
      dispatch(updateIsLoading(false));
    }
  };

  const handleFileUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const files = e.target.files;
    if (files && files.length > 0) {
      dispatch(updateIsLoading(true));
      setValue("file", files);
      trigger("file");
      setToastData({
        message: `File "${files[0].name}" selected successfully`,
        status: "Success",
      });
      setTimeout(() => {
        setToastData(constants.defaultToastData);
      }, 2000);
    }
    dispatch(updateIsLoading(false));
  };

  const onChangeField = React.useCallback(
    (name: any) => (text: any) => {
      setValue(name, text);
      isSubmitted && trigger(name);
    },
    [setValue, isSubmitted, trigger]
  );

  return (
    <>
      {toastData?.message && (
        <ToastNotification
          icon={toastData.status === "Success" ? successToast : errorToast}
          content={toastData?.message}
          position={"top-right"}
          autoClose={2000}
          handleClose={() => setToastData(constants.defaultToastData)}
        />
      )}
      <Grid
        container
        flexDirection="column"
        gap="8px"
        style={{
          backgroundColor: "#FFFFFF",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
          maxWidth: "400px",
          marginBottom: "16px",
          ...(!accessToken && {
            filter: "grayscale(100%)",
            pointerEvents: "none",
          }),
        }}
      >
        <form>
          {/* // <!-- Select Outlet of buyer --> */}
          <div className={styles.formFieldItem}>
            <InputLabel id="demo-simple-select-label">Outlet</InputLabel>
            <div>
              <Select
                fullWidth
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                onChange={(e: SelectChangeEvent<string>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log("selected value", e.target.value);
                  onChangeField("outletId")(e.target.value);
                }}
                value={watch("outletId") || "asdasd"}
                error={!!errors.outletId}
              >
                {outlets.map((outlet) => (
                  <MenuItem key={outlet.id} value={outlet.cartId}>
                    {outlet.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.outletId && (
                <div className={styles.errorMessage}>
                  {errors.outletId.message}
                </div>
              )}
            </div>
          </div>

          {/* File Upload Section */}
          <div className={styles.formFieldItem} style={{ marginTop: "16px" }}>
            <InputLabel>Upload File</InputLabel>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Button
                label="Choose File"
                id="choose-file-button"
                handleClick={handleFileUploadClick}
                large={false}
                type="button"
              />
              <span style={{ fontSize: "14px", color: "#666" }}>
                {watch("file") && (watch("file") as FileList).length > 0
                  ? (watch("file") as FileList)[0].name
                  : "No file selected"}
              </span>
            </div>
            {errors.file && (
              <div className={styles.errorMessage}>{errors.file.message}</div>
            )}
            <input
              ref={fileInputRef}
              style={{ display: "none" }}
              type="file"
              multiple={false}
              accept=".pdf"
              onChange={onFileUpload}
              id="file-input"
            />
          </div>

          <div
            className={`${styles.formField} ${styles.formField}`}
            id="button-signin"
            style={{ marginTop: "16px" }}
          >
            <Button
              label="Add to Cart"
              large={true}
              handleClick={handleSubmit(onSubmit)}
              id="add-to-cart-button-submit"
              type="submit"
            />
          </div>
        </form>
      </Grid>
    </>
  );
}

export default BuyerCartForm;
